import clsx from 'clsx';
import { EllipsisHorizontalIcon } from '@heroicons/react/16/solid';
import { Badge } from '../../common/Catalyst/badge';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../../common/Catalyst/dropdown';
import { CommentReturn } from '../../clients/rust-api-client';
import { formatRelativeTime, formatTimestamp } from '../../utils/dateUtils';
import { CommentNode as Node } from './commentTree';
import { useThreadCopy } from './commentLabels';
import CommentComposer from './CommentComposer';
import AttachmentThumb from './AttachmentThumb';

interface Props {
  node: Node;
  /** The signed-in user's id, for deciding what is theirs. */
  currentUserId: string | null;
  /** Manager+ — also gates whether visibility badges are rendered at all. */
  canPostInternal: boolean;
  canModerate: boolean;
  canAttach: boolean;
  authorName: (comment: CommentReturn) => string;
  replyingToId: string | null;
  editingId: string | null;
  busy: boolean;
  onStartReply: (commentId: string) => void;
  onStartEdit: (commentId: string) => void;
  onCancel: () => void;
  onReply: (parent: CommentReturn, content: string) => Promise<string | void>;
  onEdit: (comment: CommentReturn, content: string) => Promise<string | void>;
  onDelete: (comment: CommentReturn) => void;
  /** The comment to outline: the one a notification pointed at. */
  highlightedId?: string | null;
}

/** The element id a comment renders under, for scrolling to it. */
export const commentAnchor = (commentId: string): string => `comment-${commentId}`;

export const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('') || '?';

export const Avatar: React.FC<{ name: string; size?: 'sm' | 'md' }> = ({ name, size = 'md' }) => (
  <span
    aria-hidden
    className={clsx(
      'flex shrink-0 items-center justify-center rounded-full bg-fg/10 font-semibold text-fg-secondary',
      size === 'sm' ? 'h-5 w-5 text-[9px]' : 'h-6 w-6 text-[10px]'
    )}
  >
    {initialsOf(name)}
  </span>
);

/**
 * One comment inside a card: who, when, the ··· menu, then the text.
 *
 * The rules are the thread's, unchanged from before the restyle:
 * management may soft-delete a homeowner's comment but never rewrite its
 * words (the backend answers 403), and only an author edits their own.
 */
function CommentBody({
  comment,
  inReplyTo,
  props,
  showAvatar,
}: {
  comment: CommentReturn;
  inReplyTo?: CommentReturn;
  props: Props;
  showAvatar: boolean;
}) {
  const { currentUserId, canPostInternal, canModerate, authorName, editingId, busy } = props;
  const copy = useThreadCopy(comment.visibility);

  // Identity, not role. `user_id` is *absent* on a homeowner's comment
  // rather than null, so it is never the thing to compare.
  const isMine =
    comment.author_realm === 'user' &&
    currentUserId !== null &&
    comment.author_id === currentUserId;

  const canEdit = isMine && !comment.is_deleted;
  const canDelete = (isMine || canModerate) && !comment.is_deleted;
  const isEditing = editingId === comment.id;
  const name = authorName(comment);

  return (
    <div className="px-3 py-2.5">
      <div className="flex items-center gap-2">
        {showAvatar && <Avatar name={name} size="sm" />}
        <span className="text-[13px] font-medium text-fg">{isMine ? 'You' : name}</span>

        {/* A board member sees one kind of comment, so labelling it tells
            them only that another kind exists. */}
        {canPostInternal && <Badge color={copy.badgeColor}>{copy.label}</Badge>}

        <time
          dateTime={comment.created_at}
          title={formatTimestamp(comment.created_at)}
          className="text-[12px] text-fg-muted"
        >
          {formatRelativeTime(comment.created_at)}
        </time>

        {comment.is_edited && (
          <span title={formatTimestamp(comment.updated_at)} className="text-[12px] text-fg-muted">
            (edited)
          </span>
        )}

        {(canEdit || canDelete) && !isEditing && (
          <Dropdown>
            <DropdownButton
              plain
              aria-label="Comment actions"
              className="ml-auto !p-1 !text-fg-muted hover:!text-fg"
              disabled={busy}
            >
              <EllipsisHorizontalIcon className="h-4 w-4" />
            </DropdownButton>
            <DropdownMenu anchor="bottom end" className="min-w-32">
              {canEdit && (
                <DropdownItem onClick={() => props.onStartEdit(comment.id)}>
                  <DropdownLabel>Edit</DropdownLabel>
                </DropdownItem>
              )}
              {canDelete && (
                <DropdownItem onClick={() => props.onDelete(comment)}>
                  <DropdownLabel>Delete</DropdownLabel>
                </DropdownItem>
              )}
            </DropdownMenu>
          </Dropdown>
        )}
      </div>

      {/* A reply that was flattened onto the root still says what it
          answered, so the argument stays followable without the indent. */}
      {inReplyTo && (
        <p className="mt-1 text-[12px] text-fg-muted">In reply to {authorName(inReplyTo)}</p>
      )}

      <div className="mt-1.5">
        {comment.is_deleted ? (
          <p className="text-[14px] italic text-fg-muted">This comment was deleted.</p>
        ) : isEditing ? (
          <CommentComposer
            visibility={comment.visibility}
            mode="edit"
            initialContent={comment.content}
            busy={busy}
            autoFocus
            onSubmit={(content) => props.onEdit(comment, content)}
            onCancel={props.onCancel}
          />
        ) : (
          <p className="whitespace-pre-wrap text-[14px] leading-6 text-fg-secondary">{comment.content}</p>
        )}
      </div>

      {!comment.is_deleted && (comment.attachments?.length ?? 0) > 0 && (
        <div className="mt-2 flex flex-wrap items-start gap-2">
          {comment.attachments!.map((attachment) => (
            <AttachmentThumb key={attachment.id} attachment={attachment} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A root comment and its replies, as one card — Linear's shape. Replies
 * sit under a hairline inside the card, and the card ends in the place to
 * add one. The tree is two levels, so replies take no replies.
 */
export function CommentNodeView(props: Props) {
  const { node, canAttach, replyingToId, busy } = props;
  const { comment } = node;
  const isReplying = replyingToId === comment.id;
  const internal = comment.visibility === 'internal';
  const highlighted = props.highlightedId === comment.id;

  return (
    <article
      id={commentAnchor(comment.id)}
      aria-current={highlighted ? 'true' : undefined}
      className={clsx(
        'min-w-0 flex-1 scroll-mt-24 overflow-hidden rounded-lg border bg-raised',
        highlighted
          ? 'border-accent ring-1 ring-accent'
          : // Peripheral vision again: an internal note never looks like
            // something the homeowner can read.
            internal && props.canPostInternal
            ? 'border-amber-500/35'
            : 'border-line'
      )}
    >
      <CommentBody comment={comment} inReplyTo={node.inReplyTo} props={props} showAvatar={false} />

      {node.replies.map((reply) => (
        <div
          key={reply.comment.id}
          id={commentAnchor(reply.comment.id)}
          aria-current={props.highlightedId === reply.comment.id ? 'true' : undefined}
          className={clsx(
            'scroll-mt-24 border-t border-line/70',
            // A reply is outlined inside its card; the card's own border
            // belongs to the comment it answers.
            props.highlightedId === reply.comment.id &&
              'rounded-md ring-2 ring-inset ring-accent'
          )}
        >
          <CommentBody
            comment={reply.comment}
            inReplyTo={reply.inReplyTo}
            props={props}
            showAvatar
          />
        </div>
      ))}

      {!comment.is_deleted &&
        (isReplying ? (
          <div className="border-t border-line/70 p-2">
            <CommentComposer
              // A reply inherits its parent's thread and cannot change it.
              // The backend requires parent and child to share a
              // visibility and answers a mismatch with a 500, so the UI's
              // job is to make that payload unconstructable.
              visibility={comment.visibility}
              replyingTo={comment.id}
              busy={busy}
              allowAttachments={canAttach}
              autoFocus
              onSubmit={(content) => props.onReply(comment, content)}
              onCancel={props.onCancel}
              // Closes once the reply and any files have landed, as in
              // Linear; a failed attach keeps it open for the retry.
              onSettled={props.onCancel}
            />
          </div>
        ) : (
          <button
            type="button"
            aria-label="Reply"
            onClick={() => props.onStartReply(comment.id)}
            className="block w-full border-t border-line/70 px-3 py-2 text-left text-[13px] text-fg-muted hover:bg-fg/[0.03] hover:text-fg-secondary"
          >
            Leave a reply…
          </button>
        ))}
    </article>
  );
}

export default CommentNodeView;
